import { z } from "zod";
import { type FacilityRole, GROUP_ROLES, type GroupRole, type RoleCode, type ScopedRole, isFacilityRole } from "./auth";
import { paginationQuery, searchParam } from "./pagination";
import type { ScopeCodedRef, ScopeRef } from "./users";

/**
 * `/user-groups` — who a scoped login is, in accreditation and competition.
 *
 * A group replaces the per-user `user_scopes` rows. What it decides:
 *   · WHERE its members may act — scope rows (organization, main function,
 *     sub-agency, sub-function, optionally one games), each readable or also
 *     writable (`can_write`, false by default);
 *   · WHAT they may do there — permissions from the catalogue below;
 *   · WHICH sports an operator group may write (OVR, TSR).
 *
 * A FACILITY group (role TRANSPORT_PROVIDER or HOTEL — `FACILITY_ROLES`)
 * holds facility rows instead (`user_group_facilities`: which transport
 * provider it is, which lodging buildings it runs (M11); M12 adds dining
 * halls), never scope rows or sports. Every card predicate stays FALSE for it.
 *
 * Rules the schema makes structural (see apps/api/src/db/schema/user-groups.ts):
 *   · One group per login — `users.user_group_id` is a column, not a link table.
 *   · One role per group — `role_code` is one of `GROUP_ROLES` (OPERATION,
 *     ORGANIZATION, TRANSPORT_PROVIDER, HOTEL), fixed at creation. The group applies
 *     only while the login's ACTIVE role equals it; acting as anything else,
 *     the group is ignored.
 *   · Deny by default — no group, an inactive group, a group of the other
 *     role, or a read-only scope row grants nothing to write.
 *
 * Only SUPER_ADMIN creates groups, edits them and puts logins in them.
 */

// ── Roles and the permission catalogue ──────────────────────────────────────

// `GROUP_ROLES` / `GroupRole` (the roles a group can be for: the scoped roles
// and the facility roles) live in ./auth, next to SCOPED_ROLES and
// FACILITY_ROLES. Derived, never re-typed (CLAUDE.md bug 4).

export const PERMISSION_AREAS = ["accreditation", "card", "competition", "logistics", "transport", "accommodation", "helpdesk", "fnb"] as const;
export type PermissionArea = (typeof PERMISSION_AREAS)[number];

/**
 * Every permission a group can hold. The database CHECK on
 * `user_group_permissions.permission` is generated from this tuple (text +
 * CHECK, never a pg enum — see "Known traps" 1), so adding one here is a new
 * migration, on purpose.
 */
export const PERMISSION_CODES = [
	"accreditation.register",
	"accreditation.submit",
	"accreditation.approve",
	"accreditation.invite",
	"accreditation.health",
	"competition.entries",
	"competition.results",
	"competition.official",
	"competition.shortlist",
	"competition.declare",
	"competition.display",
	"card.print",
	"card.activate",
	"card.report_lost",
	"logistics.travel",
	"logistics.travel_review",
	"transport.request",
	"transport.dispatch",
	"accommodation.manage",
	"accommodation.desk",
	"helpdesk.handle",
	"helpdesk.report",
	"fnb.order",
	"fnb.manage",
	"fnb.scan",
] as const;
export type PermissionCode = (typeof PERMISSION_CODES)[number];

export interface PermissionDefinition {
	area: PermissionArea;
	/** The group roles allowed to hold it. A group of any other role is refused with GROUP_ROLE_MISMATCH. */
	roles: readonly GroupRole[];
}

/**
 * The catalogue. `satisfies Record<PermissionCode, …>` makes a code without an
 * entry — or an entry without a code — a typecheck failure.
 *
 * Host-side powers are OPERATION only: approving a card (a NOC-type group can
 * never approve, B2) and every competition write.
 */
export const PERMISSIONS = {
	"accreditation.register": { area: "accreditation", roles: ["ORGANIZATION", "OPERATION"] },
	"accreditation.submit": { area: "accreditation", roles: ["ORGANIZATION", "OPERATION"] },
	"accreditation.approve": { area: "accreditation", roles: ["OPERATION"] },
	"accreditation.invite": { area: "accreditation", roles: ["ORGANIZATION", "OPERATION"] },
	/** Read medical certificates and TUEs of cards in scope: the host's medical / anti-doping desk (M4). */
	"accreditation.health": { area: "accreditation", roles: ["OPERATION"] },
	"competition.entries": { area: "competition", roles: ["OPERATION"] },
	"competition.results": { area: "competition", roles: ["OPERATION"] },
	"competition.official": { area: "competition", roles: ["OPERATION"] },
	/**
	 * M7a. Pick athletes per event (PRIMARY / RESERVE) for the organisation(s)
	 * in writable scope, and submit the sport's shortlist as final. A NOC's
	 * edits lock once final; the host (OPERATION) is bound the same way —
	 * only ADMIN / SUPER_ADMIN edit after final.
	 */
	"competition.shortlist": { area: "competition", roles: ["ORGANIZATION", "OPERATION"] },
	/**
	 * M7b. Fill in and submit the EOI (sports + estimates) and EBN (events +
	 * numbers) for the organisation(s) in WRITABLE scope — a NOC's own, or a
	 * host desk's. Kept apart from `competition.entries` (OVR writes): host
	 * operator groups hold that one with any-organisation scope rows.
	 */
	"competition.declare": { area: "competition", roles: ["ORGANIZATION", "OPERATION"] },
	/**
	 * TSR (0058). The Display Controller: drive the venue display screens and
	 * scoreboards of the group's sports (`user_group_sports`). Reads and
	 * screen control only — never a result write (that stays
	 * `competition.results`).
	 */
	"competition.display": { area: "competition", roles: ["OPERATION"] },
	/** M7a. Make print batches and mark them printed, for cards in writable scope. Host side only. */
	"card.print": { area: "card", roles: ["OPERATION"] },
	/** M7a. Activate printed cards (valid until a date) for cards in writable scope. Host side only (user, 2026-10-16). */
	"card.activate": { area: "card", roles: ["OPERATION"] },
	/** M7a. Report a card lost / stolen / damaged (and undo it within the window), for cards in writable scope. */
	"card.report_lost": { area: "card", roles: ["ORGANIZATION", "OPERATION"] },
	/**
	 * M9. Add and change arrival / departure of cards in scope (writable scope
	 * rows), also after approval: the NOC's travel desk, or a host team for
	 * its own staff. Without it, `accreditation.register` still covers travel
	 * while the card itself is editable.
	 */
	"logistics.travel": { area: "logistics", roles: ["ORGANIZATION", "OPERATION"] },
	/** M9. The host's A&D desk: see all travel in scope, override flights, set delays. Host side only. */
	"logistics.travel_review": { area: "logistics", roles: ["OPERATION"] },
	/**
	 * M10. Request venue trips for the organisation(s) in scope and follow its
	 * transfers (a NOC's own passengers only). ORGANIZATION is bound by the
	 * booking lead time (`transport_settings.noc_lead_hours`); the host is not.
	 */
	"transport.request": { area: "transport", roles: ["ORGANIZATION", "OPERATION"] },
	/**
	 * M10. Dispatch. OPERATION: the host's transport desk (suggested transfers,
	 * vehicles and drivers on trips, auto-assign, the fleet). TRANSPORT_PROVIDER:
	 * Start / Arrived on the trips of its own vehicles, nothing more.
	 */
	"transport.dispatch": { area: "transport", roles: ["OPERATION", "TRANSPORT_PROVIDER"] },
	/**
	 * M11. The host's accommodation office: buildings, rooms, reservations,
	 * contingent stay defaults, assign / move / unassign, stay edits and
	 * auto-assign. Card writes still need a writable scope row over the card
	 * (`cardWritable`); building config needs only the permission.
	 */
	"accommodation.manage": { area: "accommodation", roles: ["OPERATION"] },
	/**
	 * M11. The front desk: check in / out / undo, the AC-number lookup,
	 * arrivals, departures, in house. OPERATION: cards in its writable scope.
	 * HOTEL: assignments in its own buildings (`buildingInScope`).
	 */
	"accommodation.desk": { area: "accommodation", roles: ["OPERATION", "HOTEL"] },
	/**
	 * M11. Work helpdesk tickets: assign, prioritise, add notes, resolve.
	 * OPERATION: tickets whose organisation is in scope (a scope row with any
	 * organisation sees all). HOTEL: tickets of its own buildings.
	 */
	"helpdesk.handle": { area: "helpdesk", roles: ["OPERATION", "HOTEL"] },
	/**
	 * M11. Report a problem (a room where its own people stay) and follow its
	 * own organisation's tickets. 0025 backfills it to ORGANIZATION groups that
	 * hold `accreditation.register`.
	 */
	"helpdesk.report": { area: "helpdesk", roles: ["ORGANIZATION", "OPERATION"] },
	/**
	 * M12. Order packed (takeaway) meals for the organisation(s) in writable
	 * scope and follow them: a NOC's orders start PENDING and it may cancel
	 * them until the meal starts. Also opens "Where your delegation eats".
	 */
	"fnb.order": { area: "fnb", roles: ["ORGANIZATION", "OPERATION"] },
	/**
	 * M12. The host's F&B desk: dining halls and F&B settings, the meal plan,
	 * deciding takeaway orders (confirm / reject / collected / cancel) for the
	 * organisations in scope, ordering confirmed packs. Host side only.
	 */
	"fnb.manage": { area: "fnb", roles: ["OPERATION"] },
	/**
	 * M12. The kitchen door: scan meal passes at a hall. Needs the card in
	 * read scope too — a door group gets read-only scope rows covering every
	 * category (or organisation) of its games (a row must name at least one
	 * of organisation / category / federation / function). Host side only.
	 */
	"fnb.scan": { area: "fnb", roles: ["OPERATION"] },
} as const satisfies Record<PermissionCode, PermissionDefinition>;

/**
 * What a group of each role holds besides permissions. Scope rows: the scoped
 * roles. Sports: OPERATION only (competition writes). Facility rows: the
 * facility roles. Anything else is refused by the API (422 GROUP_ROLE_MISMATCH).
 */
export const groupHoldsScopes = (role: GroupRole): role is ScopedRole => !isFacilityRole(role);
export const groupHoldsSports = (role: GroupRole): boolean => role === "OPERATION";
export const groupHoldsFacilities = (role: GroupRole): role is FacilityRole => isFacilityRole(role);

/** Whether a group whose role is `role` may hold `code`. */
export const permissionAllowedFor = (code: PermissionCode, role: RoleCode): boolean =>
	(PERMISSIONS[code].roles as readonly RoleCode[]).includes(role);

/** The codes a group of `role` may hold, in catalogue order — for the permissions picker. */
export const permissionsForRole = (role: RoleCode): PermissionCode[] =>
	PERMISSION_CODES.filter((code) => permissionAllowedFor(code, role));

// ── Limits ──────────────────────────────────────────────────────────────────

export const GROUP_NAME_MAX = 120;
export const GROUP_DESCRIPTION_MAX = 500;
/** Scope rows one `PUT /user-groups/:id/scopes` may carry. */
export const GROUP_SCOPES_MAX = 200;
/** Sports one `PUT /user-groups/:id/sports` may carry. */
export const GROUP_SPORTS_MAX = 200;
/** Facility rows one `PUT /user-groups/:id/facilities` may carry. */
export const GROUP_FACILITIES_MAX = 50;

/**
 * The kinds of facility row, and the one a facility role's group holds:
 * `transport_provider` (M10, TRANSPORT_PROVIDER), `lodging_building` (M11,
 * HOTEL). M12 adds `dining_hall`. Each kind is one typed FK column of
 * `user_group_facilities`, named `<kind>_id`.
 */
export const FACILITY_KINDS = ["transport_provider", "lodging_building"] as const;
export type FacilityKind = (typeof FACILITY_KINDS)[number];
export const FACILITY_KIND_OF_ROLE = {
	TRANSPORT_PROVIDER: "transport_provider",
	HOTEL: "lodging_building",
} as const satisfies Record<FacilityRole, FacilityKind>;

// ── List query ──────────────────────────────────────────────────────────────

export const USER_GROUP_SORT_KEYS = ["name", "role_code", "member_count", "created_at", "updated_at"] as const;
export type UserGroupSortKey = (typeof USER_GROUP_SORT_KEYS)[number];

/**
 * `GET /user-groups`. Offset pagination: a small admin lookup where people want
 * page numbers. `is_active` is the query-string spelling of a boolean.
 */
export const listUserGroupsQuery = paginationQuery.extend({
	search: searchParam.optional(),
	role_code: z.enum(GROUP_ROLES).optional(),
	is_active: z.enum(["true", "false"]).optional(),
	sort: z.enum(USER_GROUP_SORT_KEYS).optional(),
	dir: z.enum(["asc", "desc"]).optional(),
});
export type ListUserGroupsQuery = z.infer<typeof listUserGroupsQuery>;

// ── Responses ───────────────────────────────────────────────────────────────

/** A group as other payloads mention it (`/users` rows, `/auth/me`). */
export interface UserGroupRef {
	user_group_id: string;
	name: string;
	role_code: GroupRole;
	is_active: boolean;
}

/** One row of `GET /user-groups`. */
export interface UserGroupListItem extends UserGroupRef {
	description: string | null;
	/** Logins whose `user_group_id` is this group. A group with members cannot be deleted. */
	member_count: number;
	scope_count: number;
	permission_count: number;
	sport_count: number;
	created_at: string;
	updated_at: string;
}

/**
 * One scope row of a group. Every reference null = "any" on that column; at
 * least one of the four accreditation columns is always set. `games` null =
 * both games. `can_write` false = the group may see, not register.
 */
export interface GroupScope {
	user_group_scope_id: string;
	responsible_organization: ScopeRef | null;
	main_function: ScopeCodedRef | null;
	sub_agency: ScopeRef | null;
	sub_function: ScopeCodedRef | null;
	games: { games_id: string; code: string; name: string } | null;
	can_write: boolean;
}

export interface GroupSport {
	sport_id: string;
	code: string;
	name: string;
}

/**
 * One facility row of a FACILITY group (`user_group_facilities`, CHECK
 * `num_nonnulls(…) = 1`): exactly one reference is set — the one of the
 * group's role's kind (`FACILITY_KIND_OF_ROLE`). M12 adds `dining_hall`.
 */
export interface GroupFacility {
	user_group_facility_id: string;
	transport_provider: { transport_provider_id: string; name: string } | null;
	/** M11: a HOTEL group's lodging building. */
	lodging_building: { lodging_building_id: string; name: string } | null;
}

/** `GET /user-groups/:id` — the list row plus what the group grants. */
export interface UserGroupDetail extends UserGroupListItem {
	scopes: GroupScope[];
	permissions: PermissionCode[];
	sports: GroupSport[];
	/** Facility groups only (TRANSPORT_PROVIDER, HOTEL); `[]` for every other group. */
	facilities: GroupFacility[];
}

// ── Write requests ──────────────────────────────────────────────────────────
// Message-factory pattern (see `makeCreateUserForm` in ./users): English
// defaults, so the API and the default exports keep the English wire copy
// while the web app passes translated copy. Every string is trimmed BEFORE its
// length check (CLAUDE.md bug 5).

export interface UserGroupFormMessages {
	nameRequired: string;
	nameMax: string;
	descriptionMax: string;
	roleRequired: string;
	/** A scope row with none of organization / main function / sub-agency / sub-function. */
	scopeEmpty: string;
	scopesMax: string;
	scopeDuplicate: string;
	permissionDuplicate: string;
	sportsMax: string;
	sportDuplicate: string;
}

export const enUserGroupFormMessages: UserGroupFormMessages = {
	nameRequired: "Enter a name",
	nameMax: `At most ${GROUP_NAME_MAX} characters`,
	descriptionMax: `At most ${GROUP_DESCRIPTION_MAX} characters`,
	roleRequired: "Pick a role",
	scopeEmpty: "Pick at least one of organization, main function, sub-agency or sub-function",
	scopesMax: `At most ${GROUP_SCOPES_MAX} scope rows per group`,
	scopeDuplicate: "This scope row is listed twice",
	permissionDuplicate: "A permission is listed twice",
	sportsMax: `At most ${GROUP_SPORTS_MAX} sports per group`,
	sportDuplicate: "A sport is listed twice",
};

const groupName = (m: UserGroupFormMessages) => z.string().trim().min(1, m.nameRequired).max(GROUP_NAME_MAX, m.nameMax);
const groupDescription = (m: UserGroupFormMessages) => z.string().trim().max(GROUP_DESCRIPTION_MAX, m.descriptionMax);
const groupRole = (m: UserGroupFormMessages) => z.enum(GROUP_ROLES, { errorMap: () => ({ message: m.roleRequired }) });

/** The create form. `description` binds as `""`; `toCreateUserGroupBody` drops it. */
export function makeCreateUserGroupForm(m: UserGroupFormMessages = enUserGroupFormMessages) {
	return z.object({
		name: groupName(m),
		description: groupDescription(m),
		role_code: groupRole(m),
		is_active: z.boolean(),
	});
}
export const createUserGroupForm = makeCreateUserGroupForm();
export type CreateUserGroupForm = z.infer<typeof createUserGroupForm>;

/** The edit form. No `role_code`: a group's role is fixed at creation. */
export function makeUpdateUserGroupForm(m: UserGroupFormMessages = enUserGroupFormMessages) {
	return z.object({
		name: groupName(m),
		description: groupDescription(m),
		is_active: z.boolean(),
	});
}
export const updateUserGroupForm = makeUpdateUserGroupForm();
export type UpdateUserGroupForm = z.infer<typeof updateUserGroupForm>;

/** Body of `POST /user-groups` — the wire schema the API validates with. */
export function makeCreateUserGroupRequest(m: UserGroupFormMessages = enUserGroupFormMessages) {
	return z.object({
		name: groupName(m),
		description: groupDescription(m).nullish(),
		role_code: groupRole(m),
		is_active: z.boolean().default(true),
	});
}
export const createUserGroupRequest = makeCreateUserGroupRequest();
export type CreateUserGroupBody = z.input<typeof createUserGroupRequest>;

/**
 * Body of `PATCH /user-groups/:id`. `role_code` is NOT accepted — changing it
 * would silently re-interpret every scope row and permission the group holds,
 * and could leave it holding permissions its new role may not have. A group
 * for the other role is a new group. `description: null` clears it.
 *
 * A body that sends `role_code` anyway is REFUSED (422), not stripped: a
 * client that believes it changed the role must hear that it did not. Only
 * the wire schema says so — the edit form has no role field to send.
 */
export const ROLE_CODE_IMMUTABLE_MESSAGE = "A group's role cannot be changed. Create a new group for the other role.";

export function makeUpdateUserGroupRequest(m: UserGroupFormMessages = enUserGroupFormMessages) {
	return z.object({
		name: groupName(m).optional(),
		description: groupDescription(m).nullish(),
		is_active: z.boolean().optional(),
		/** Any value — even the group's current role — fails; only an absent key passes. */
		role_code: z.never({ errorMap: () => ({ message: ROLE_CODE_IMMUTABLE_MESSAGE }) }).optional(),
	});
}
export const updateUserGroupRequest = makeUpdateUserGroupRequest();
export type UpdateUserGroupBody = z.input<typeof updateUserGroupRequest>;

export const toCreateUserGroupBody = (f: CreateUserGroupForm): CreateUserGroupBody => ({
	name: f.name,
	...(f.description ? { description: f.description } : {}),
	role_code: f.role_code,
	is_active: f.is_active,
});

/** Only what actually changed, so the audit row's `changed_fields` names the edit. */
export function toUpdateUserGroupBody(f: UpdateUserGroupForm, before: UserGroupListItem): UpdateUserGroupBody {
	const body: UpdateUserGroupBody = {};
	if (f.name !== before.name) body.name = f.name;
	if (f.is_active !== before.is_active) body.is_active = f.is_active;
	const description = f.description === "" ? null : f.description;
	if (description !== before.description) body.description = description;
	return body;
}

const optionalId = z.string().uuid().nullish();

/** A scope row's identity — the same five columns the database's unique grant index covers. */
const scopeKey = (s: {
	responsible_organization_id?: string | null;
	main_function_id?: string | null;
	sub_agency_id?: string | null;
	sub_function_id?: string | null;
	games_id?: string | null;
}) =>
	[s.responsible_organization_id, s.main_function_id, s.sub_agency_id, s.sub_function_id, s.games_id]
		.map((v) => v ?? "")
		.join("|");

/**
 * One item of `PUT /user-groups/:id/scopes`. At least one of the four
 * accreditation ids must be set — `games_id` alone would mean "everything in
 * that games", which is a role, not a scope. `can_write` is required: the
 * admin decides every time, nothing defaults to writable.
 */
export function makeGroupScopeItem(m: UserGroupFormMessages = enUserGroupFormMessages) {
	return z
		.object({
			responsible_organization_id: optionalId,
			main_function_id: optionalId,
			sub_agency_id: optionalId,
			sub_function_id: optionalId,
			games_id: optionalId,
			can_write: z.boolean(),
		})
		.refine((s) => s.responsible_organization_id != null || s.main_function_id != null || s.sub_agency_id != null || s.sub_function_id != null, {
			message: m.scopeEmpty,
		});
}
export const groupScopeItem = makeGroupScopeItem();
export type GroupScopeBody = z.input<typeof groupScopeItem>;

/** Body of `PUT /user-groups/:id/scopes` — replace-all. An empty list removes every row (the group then sees nothing). */
export function makeSetGroupScopesRequest(m: UserGroupFormMessages = enUserGroupFormMessages) {
	return z.object({
		scopes: z
			.array(makeGroupScopeItem(m))
			.max(GROUP_SCOPES_MAX, m.scopesMax)
			.refine((rows) => new Set(rows.map(scopeKey)).size === rows.length, { message: m.scopeDuplicate }),
	});
}
export const setGroupScopesRequest = makeSetGroupScopesRequest();
export type SetGroupScopesBody = z.input<typeof setGroupScopesRequest>;

/**
 * Body of `PUT /user-groups/:id/permissions` — replace-all. Codes the group's
 * role may not hold are refused by the API with GROUP_ROLE_MISMATCH (the
 * schema cannot see the group).
 */
export function makeSetGroupPermissionsRequest(m: UserGroupFormMessages = enUserGroupFormMessages) {
	return z.object({
		permissions: z
			.array(z.enum(PERMISSION_CODES))
			.max(PERMISSION_CODES.length)
			.refine((codes) => new Set(codes).size === codes.length, { message: m.permissionDuplicate }),
	});
}
export const setGroupPermissionsRequest = makeSetGroupPermissionsRequest();
export type SetGroupPermissionsBody = z.input<typeof setGroupPermissionsRequest>;

/** Body of `PUT /user-groups/:id/sports` — replace-all. */
export function makeSetGroupSportsRequest(m: UserGroupFormMessages = enUserGroupFormMessages) {
	return z.object({
		sport_ids: z
			.array(z.string().uuid())
			.max(GROUP_SPORTS_MAX, m.sportsMax)
			.refine((ids) => new Set(ids).size === ids.length, { message: m.sportDuplicate }),
	});
}
export const setGroupSportsRequest = makeSetGroupSportsRequest();
export type SetGroupSportsBody = z.input<typeof setGroupSportsRequest>;

/**
 * Body of `PUT /users/:id/group`. `null` takes the login out of its group. A
 * login that does not hold the group's role is refused with GROUP_ROLE_MISMATCH.
 */
export const setUserGroupRequest = z.object({
	user_group_id: z.string().uuid().nullable(),
});
export type SetUserGroupBody = z.input<typeof setUserGroupRequest>;

// ── Facility rows (facility groups only) ────────────────────────────────────

export interface GroupFacilityFormMessages {
	/** A row that names no facility. */
	facilityRequired: string;
	facilitiesMax: string;
	facilityDuplicate: string;
}

export const enGroupFacilityFormMessages: GroupFacilityFormMessages = {
	facilityRequired: "Pick exactly one transport company or building per row",
	facilitiesMax: `At most ${GROUP_FACILITIES_MAX} rows per group`,
	facilityDuplicate: "This facility is listed twice",
};

/** The id column of each facility kind, in FACILITY_KINDS order (`user_group_facilities.<kind>_id`). */
export const FACILITY_ID_KEYS = ["transport_provider_id", "lodging_building_id"] as const satisfies readonly `${FacilityKind}_id`[];
export type FacilityIdKey = (typeof FACILITY_ID_KEYS)[number];

/** A facility row's identity: its kind and id — the `uq_user_group_facilities_*` indexes. */
const facilityKey = (f: Partial<Record<FacilityIdKey, string | null>>) => FACILITY_ID_KEYS.map((k) => f[k] ?? "").join("|");

/**
 * One item of `PUT /user-groups/:id/facilities`. Exactly one reference is set
 * (the database CHECK `ck_user_group_facilities_one` is `num_nonnulls(…) = 1`
 * over the same columns). Which kind a group may hold is its role's
 * (`FACILITY_KIND_OF_ROLE`): the API refuses the other with GROUP_ROLE_MISMATCH.
 */
export function makeGroupFacilityItem(m: GroupFacilityFormMessages = enGroupFacilityFormMessages) {
	return z
		.object({
			transport_provider_id: z.string().uuid().nullish(),
			lodging_building_id: z.string().uuid().nullish(),
		})
		.refine((f) => FACILITY_ID_KEYS.filter((k) => f[k] != null).length === 1, { message: m.facilityRequired });
}
export const groupFacilityItem = makeGroupFacilityItem();
export type GroupFacilityBody = z.input<typeof groupFacilityItem>;

/**
 * Body of `PUT /user-groups/:id/facilities` — replace-all. Only a facility
 * group (TRANSPORT_PROVIDER, HOTEL) may hold rows, and only of its role's kind
 * (`FACILITY_KIND_OF_ROLE`); anything else is 422 GROUP_ROLE_MISMATCH. An
 * empty list removes every row (the group then sees nothing).
 */
export function makeSetGroupFacilitiesRequest(m: GroupFacilityFormMessages = enGroupFacilityFormMessages) {
	return z.object({
		facilities: z
			.array(makeGroupFacilityItem(m))
			.max(GROUP_FACILITIES_MAX, m.facilitiesMax)
			.refine((rows) => new Set(rows.map(facilityKey)).size === rows.length, { message: m.facilityDuplicate }),
	});
}
export const setGroupFacilitiesRequest = makeSetGroupFacilitiesRequest();
export type SetGroupFacilitiesBody = z.input<typeof setGroupFacilitiesRequest>;
